import React from "react";
import {
  Box,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import StarIcon from "@mui/icons-material/Star";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PricingPlans from "../../PricingPlans";

// Stats
const stats = [
  { icon: <GroupsIcon sx={{ fontSize: 36, color: "#016551" }} />,      value: "10,000+", label: "Active Traders" },
  { icon: <TrendingUpIcon sx={{ fontSize: 36, color: "#016551" }} />,  value: "4",       label: "Flexible Plans" },
  { icon: <VerifiedUserIcon sx={{ fontSize: 36, color: "#016551" }} />,value: "99.9%",   label: "Uptime Guaranteed" },
  { icon: <SupportAgentIcon sx={{ fontSize: 36, color: "#016551" }} />,value: "24/7",    label: "Customer Support" },
];

// Feature comparison — real plan data
const comparisonRows = [
  { feature: "RA Calls (CASH + FUTURES)",             lite: true,  pro: true,  plus: true,  option: true  },
  { feature: "RA Automated Signals",                  lite: "300", pro: "1000",plus: "600", option: true  },
  { feature: "Signal Access — NSE Equity",            lite: true,  pro: true,  plus: true,  option: true  },
  { feature: "Signal Access — NIFTY / BANKNIFTY",     lite: false, pro: true,  plus: true,  option: true  },
  { feature: "Signal Access — FINNIFTY",              lite: false, pro: true,  plus: true,  option: true  },
  { feature: "Signal Access — MCX (Gold/Silver/Gas)", lite: false, pro: true,  plus: false, option: true  },
  { feature: "Stock Screener",                        lite: true,  pro: true,  plus: true,  option: true  },
  { feature: "Stock Charts",                          lite: true,  pro: true,  plus: true,  option: true  },
  { feature: "Watchlist & Alerts",                    lite: false, pro: true,  plus: true,  option: true  },
  { feature: "Options Chain Analysis",                lite: false, pro: false, plus: false, option: true  },
  { feature: "Options Trade Signals",                 lite: false, pro: false, plus: false, option: true  },
  { feature: "Priority Support",                      lite: false, pro: true,  plus: true,  option: true  },
  { feature: "Validity",                              lite: "3 Mo",pro: "1 Yr",plus: "6 Mo",option: "—"  },
];

const planHeaders = ["Lite", "Pro", "Plus", "Options Add-on"];

// FAQ
const faqs = [
  { q: "What is included in all plans?",              a: "All plans include Unlimited RA Calls (CASH + FUTURES), RA Automated Signals, Signal Access to NSE Equity, and access to our Stock Screener & Charts." },
  { q: "What is the difference between Lite, Pro and Plus?", a: "Lite is a 3-month starter plan (₹1,249). Plus is a 6-month plan (₹2,498). Pro is the most popular 1-year plan (₹2,999) with the best value — includes NIFTY, BANKNIFTY, FINNIFTY and MCX signals." },
  { q: "What is the Options Add-on plan?",            a: "The Options Add-on gives you access to Options Chain Analysis and Options Trade Signals. It can be added on top of any existing plan." },
  { q: "What payment methods are accepted?",          a: "We accept all major credit/debit cards, UPI, and net banking through our secure payment gateway. All transactions are encrypted and safe." },
  { q: "Can I cancel my subscription?",               a: "Yes, you can cancel at any time. Your plan remains active until the end of the current billing period. No hidden charges." },
  { q: "Does the Pro plan really save money?",        a: "Yes! Pro plan (1 Year) is originally ₹4,999 — currently available at ₹2,999 with ₹2,000 OFF. That's the best deal across all plans." },
];

const PricingSection = () => {
  const scrollToPlans = () => {
    document.getElementById("pricing-plans-anchor")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box>

      {/* ════════════════════════════════
          HERO
      ════════════════════════════════ */}
      <Box sx={{
        background: "linear-gradient(135deg, #03272c 0%, #04343a 55%, #015544 100%)",
        py: { xs: 4, md: 6 },
        minHeight: { md: 560 },
        display: "flex",
        alignItems: "center",
      }}>

        <Box sx={{ maxWidth: 1530, width: "100%", mx: "auto", boxSizing: "border-box", px: { xs: 3, md: 6 }, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4, flexWrap: { xs: "wrap", md: "nowrap" } }}>

          {/* ── LEFT ── */}
          <Box sx={{ flex: "1 1 420px", minWidth: 0 }}>
            {/* pill badge */}
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8, background: "rgba(163,230,53,0.1)", border: "1px solid rgba(163,230,53,0.28)", borderRadius: "20px", px: 2, py: 0.7, mb: 3.5 }}>
              <FlashOnIcon sx={{ color: "#a3e635", fontSize: 14 }} />
              <Typography sx={{ color: "#a3e635", fontWeight: 700, fontSize: "0.7rem", letterSpacing: 2.5 }}>EASY PAYMENT</Typography>
            </Box>

            {/* headline */}
            <Typography variant="h2" sx={{ color: "#fff", fontWeight: 900, mb: 2.5, fontSize: { xs: "2.5rem", md: "3.8rem" }, lineHeight: 1.08, letterSpacing: -1.5 }}>
              Fast, Secure &amp;{" "}
              <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
                <Box component="span" sx={{ color: "#a3e635" }}>Easy Plans.</Box>
                {/* underline accent */}
                <Box sx={{ position: "absolute", bottom: -4, left: 0, width: "100%", height: 3, background: "linear-gradient(90deg,#a3e635 0%,rgba(163,230,53,0.1) 100%)", borderRadius: 4 }} />
              </Box>
            </Typography>

            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.58)", mb: 5, maxWidth: 430, lineHeight: 1.9, fontSize: "1rem" }}>
              From first-time investors to professional options traders — pick a plan tailored for your trading journey and get started instantly.
            </Typography>

            {/* CTA row */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 5 }}>
              <Box component="button" onClick={scrollToPlans} sx={{
                background: "linear-gradient(135deg,#a3e635 0%,#84cc16 100%)",
                color: "#04343a", border: "none", borderRadius: "10px",
                px: 4, py: 1.65, fontWeight: 800, fontSize: "0.9rem", cursor: "pointer", letterSpacing: 1.2,
                boxShadow: "0 6px 22px rgba(163,230,53,0.42)",
                transition: "all 0.22s",
                "&:hover": { background: "linear-gradient(135deg,#bef264,#a3e635)", transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(163,230,53,0.52)" },
              }}>VIEW PLANS</Box>
              <Box component="button" sx={{
                background: "rgba(255,255,255,0.05)", color: "#fff",
                border: "1.5px solid rgba(255,255,255,0.22)", borderRadius: "10px",
                px: 4, py: 1.65, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                backdropFilter: "blur(8px)", transition: "all 0.22s",
                "&:hover": { borderColor: "rgba(255,255,255,0.55)", background: "rgba(255,255,255,0.09)" },
              }}>▶ WATCH VIDEO</Box>
            </Box>

            {/* divider */}
            <Box sx={{ width: 48, height: 1.5, background: "rgba(255,255,255,0.12)", mb: 3, borderRadius: 4 }} />

            {/* avatars + trust text */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 38, height: 38, fontSize: "0.8rem", border: "2px solid rgba(163,230,53,0.35)", fontWeight: 700 } }}>
                {[{ l: "S", bg: "#065f46" }, { l: "R", bg: "#1e3a5f" }, { l: "A", bg: "#4c1d95" }, { l: "M", bg: "#7f1d1d" }, { l: "K", bg: "#1c3a2a" }].map(({ l, bg }) => (
                  <Avatar key={l} sx={{ bgcolor: bg, color: "#a3e635" }}>{l}</Avatar>
                ))}
              </AvatarGroup>
              <Box>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2 }}>10,000+ Active Traders</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.38)", fontSize: "0.73rem", mt: 0.3 }}>Join them today · NSE &amp; BSE covered</Typography>
              </Box>
            </Box>
          </Box>

          {/* ── RIGHT: phone mockup ── */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexShrink: 0 }}>
            {/* outer wrapper — wide enough for phone + floating card */}
            <Box sx={{ position: "relative", width: 440, maxWidth: "100%", mt: { xs: 5, md: 0 }, mb: { xs: 4, md: 0 }, flexShrink: 0 }}>

              {/* ── "Payment Received" floating card (top-right, inside wrapper) ── */}
              <Box sx={{
                position: "absolute", top: 40, right: 0, zIndex: 5,
                background: "#fff", borderRadius: "16px", p: 2,
                boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
                minWidth: 170,
              }}>
                <Typography sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "0.82rem", mb: 0.5 }}>Payment Received</Typography>
                <Typography sx={{ fontWeight: 900, color: "#04343a", fontSize: "1.3rem", lineHeight: 1, mb: 0.5 }}>+₹2,999.00</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mt: 0.5 }}>
                  <Typography sx={{ color: "#64748b", fontSize: "0.72rem" }}>Pro Plan · 1 Year</Typography>
                  <Typography sx={{ color: "#016551", fontWeight: 800, fontSize: "0.72rem" }}>3.09% ↗</Typography>
                </Box>
              </Box>

              {/* ── PHONE FRAME ── */}
              <Box sx={{
                width: 260,
                mx: "auto",
                background: "#0f1a1c",
                borderRadius: "44px",
                border: "6px solid #1e2e30",
                boxShadow: "0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06), inset 0 0 0 1px rgba(255,255,255,0.04)",
                overflow: "hidden",
                position: "relative",
              }}>
                {/* notch */}
                <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5, pb: 0.5 }}>
                  <Box sx={{ width: 90, height: 22, background: "#0f1a1c", borderRadius: "0 0 16px 16px", border: "2px solid #1e2e30", borderTop: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#1e2e30" }} />
                    <Box sx={{ width: 40, height: 5, borderRadius: 4, background: "#1e2e30" }} />
                  </Box>
                </Box>

                {/* SCREEN CONTENT */}
                <Box sx={{ background: "linear-gradient(180deg,#04343a 0%,#052d32 100%)", px: 2, pb: 2.5 }}>

                  {/* top bar */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: "50%", background: "linear-gradient(135deg,#a3e635,#84cc16)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: "0.65rem", fontWeight: 900, color: "#04343a" }}>SY</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.58rem", lineHeight: 1 }}>Hello Rahul</Typography>
                        <Typography sx={{ color: "#fff", fontSize: "0.7rem", fontWeight: 700, lineHeight: 1.2 }}>Welcome Back</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography sx={{ fontSize: "0.7rem" }}>🔔</Typography>
                    </Box>
                  </Box>

                  {/* Active Plan card */}
                  <Box sx={{
                    background: "linear-gradient(135deg,#016551 0%,#028a6b 50%,#014d3d 100%)",
                    borderRadius: "16px", p: 2, mb: 1.5,
                    boxShadow: "0 8px 24px rgba(1,101,81,0.4)",
                    position: "relative", overflow: "hidden",
                  }}>
                    <Box sx={{ position: "absolute", top: -18, right: -18, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.06)" }} />
                    <Box sx={{ position: "absolute", bottom: -24, left: -10, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.6rem", letterSpacing: 1.5 }}>ACTIVE PLAN</Typography>
                      <Box sx={{ background: "rgba(255,165,0,0.25)", borderRadius: "6px", px: 0.8, py: 0.2 }}>
                        <Typography sx={{ color: "#ffd700", fontWeight: 800, fontSize: "0.55rem" }}>MOST POPULAR</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: "1.3rem", letterSpacing: -0.5, lineHeight: 1.1 }}>Pro Plan</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.4, mb: 1 }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.58rem", textDecoration: "line-through" }}>₹4,999</Typography>
                      <Typography sx={{ color: "#a3e635", fontWeight: 800, fontSize: "0.75rem" }}>₹2,999</Typography>
                      <Box sx={{ background: "rgba(220,38,38,0.3)", borderRadius: "4px", px: 0.6, py: 0.1 }}>
                        <Typography sx={{ color: "#fca5a5", fontWeight: 800, fontSize: "0.52rem" }}>2000 OFF</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                      <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.6rem" }}>1 Year Plan · Valid till Mar 2026</Typography>
                      <Box sx={{ background: "rgba(163,230,53,0.2)", borderRadius: "8px", px: 1, py: 0.4 }}>
                        <Typography sx={{ color: "#a3e635", fontWeight: 800, fontSize: "0.62rem" }}>ACTIVE ●</Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* quick stats row */}
                  <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                    {[
                      { icon: "📈", label: "Screener", val: "ON" },
                      { icon: "⚡", label: "Alerts", val: "ON" },
                      { icon: "📊", label: "Charts", val: "ON" },
                    ].map((s) => (
                      <Box key={s.label} sx={{ flex: 1, background: "rgba(255,255,255,0.06)", borderRadius: "10px", p: 1, textAlign: "center", border: "1px solid rgba(255,255,255,0.07)" }}>
                        <Typography sx={{ fontSize: "0.75rem" }}>{s.icon}</Typography>
                        <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.52rem", mt: 0.2 }}>{s.label}</Typography>
                        <Typography sx={{ color: "#a3e635", fontWeight: 800, fontSize: "0.6rem" }}>{s.val}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* recent payments label */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.65rem", fontWeight: 700 }}>Recent Payments</Typography>
                    <Typography sx={{ color: "#a3e635", fontSize: "0.6rem", fontWeight: 700 }}>See all</Typography>
                  </Box>

                  {/* plan payment rows */}
                  {[
                    { icon: <StarIcon sx={{ fontSize: 13, color: "#4ade80" }} />,       bg: "rgba(74,222,128,0.15)",   name: "Pro Plan",     date: "1 Mar 2026 · 1 Yr", amount: "₹2,999" },
                    { icon: <RocketLaunchIcon sx={{ fontSize: 13, color: "#a78bfa" }} />,bg: "rgba(167,139,250,0.15)", name: "Plus Plan",    date: "1 Sep 2025 · 6 Mo", amount: "₹2,498" },
                    { icon: <FlashOnIcon sx={{ fontSize: 13, color: "#94a3b8" }} />,    bg: "rgba(148,163,184,0.15)", name: "Lite Plan",    date: "1 Jun 2025 · 3 Mo", amount: "₹1,249" },
                  ].map((row) => (
                    <Box key={row.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, background: "rgba(255,255,255,0.04)", borderRadius: "10px", px: 1.2, py: 0.9 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 26, height: 26, borderRadius: "8px", background: row.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{row.icon}</Box>
                        <Box>
                          <Typography sx={{ color: "#fff", fontSize: "0.65rem", fontWeight: 700, lineHeight: 1 }}>{row.name}</Typography>
                          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.55rem" }}>{row.date}</Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ color: "#4ade80", fontWeight: 800, fontSize: "0.68rem" }}>{row.amount}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ════════════════════════════════
          STATS ROW
      ════════════════════════════════ */}
      <Box sx={{ background: "#fff", py: 5, px: { xs: 2, md: 8 }, borderBottom: "1px solid #e2e8f0" }}>
        <Grid container justifyContent="center" spacing={3}>
          {stats.map((s) => (
            <Grid item xs={6} sm={3} key={s.label} sx={{ textAlign: "center" }}>
              <Box sx={{ mb: 1 }}>{s.icon}</Box>
              <Typography variant="h4" sx={{ color: "#04343a", fontWeight: 800, lineHeight: 1.1 }}>{s.value}</Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>{s.label}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ════════════════════════════════
          PRICING PLANS (from API)
      ════════════════════════════════ */}
      <Box id="pricing-plans-anchor" sx={{ background: "#eef2f7", py: 6, px: { xs: 1, md: 2 } }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 800, color: "#04343a", mb: 1 }}>Our Plans</Typography>
        <Typography variant="body1" align="center" sx={{ color: "#64748b", mb: 4 }}>Monthly &amp; yearly billing options available</Typography>
        <PricingPlans />
      </Box>

      {/* ════════════════════════════════
          FEATURE COMPARISON TABLE
      ════════════════════════════════ */}
      <Box sx={{ background: "#fff", py: 8, px: { xs: 2, md: 8 } }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 800, color: "#04343a", mb: 1 }}>Plan Comparison</Typography>
        <Typography variant="body1" align="center" sx={{ color: "#64748b", mb: 5 }}>See exactly what's included in each plan before you decide</Typography>
        <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: "#04343a" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", minWidth: 180 }}>Feature</TableCell>
                {planHeaders.map((plan) => (
                  <TableCell key={plan} align="center" sx={{ color: "#fff", fontWeight: 700, fontSize: "0.95rem", minWidth: 110 }}>{plan}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonRows.map((row, idx) => (
                <TableRow key={row.feature} sx={{ background: idx % 2 === 0 ? "#f8fafc" : "#fff", "&:hover": { background: "#eef2f7" } }}>
                  <TableCell sx={{ fontWeight: 600, color: "#334155", py: 1.5 }}>{row.feature}</TableCell>
                  {[row.lite, row.pro, row.plus, row.option].map((val, i) => (
                    <TableCell key={i} align="center" sx={{ py: 1.5 }}>
                      {val === true  ? <CheckCircleIcon sx={{ color: "#016551", fontSize: 22 }} /> :
                       val === false ? <CancelIcon sx={{ color: "#cbd5e1", fontSize: 22 }} /> :
                       <Typography sx={{ fontWeight: 700, color: "#016551", fontSize: "0.85rem" }}>{val}</Typography>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ════════════════════════════════
          WHY STOCKYAARI
      ════════════════════════════════ */}
      <Box sx={{ background: "#eef2f7", py: 7, px: { xs: 3, md: 8 } }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 800, color: "#04343a", mb: 1 }}>Why Stockyaari?</Typography>
        <Typography variant="body1" align="center" sx={{ color: "#64748b", mb: 5 }}>Everything you need to trade smarter</Typography>
        <Grid container spacing={3} justifyContent="center">
          {[
            { icon: "🔒", title: "Secure Payments",   desc: "All transactions are encrypted and processed through trusted payment gateways." },
            { icon: "🔄", title: "Cancel Anytime",    desc: "No lock-ins. Cancel your subscription anytime with no questions asked." },
            { icon: "📈", title: "Real-Time Data",    desc: "Live stock prices, charts, and screener data updated in real time." },
            { icon: "🎯", title: "Tailored for India",desc: "NSE & BSE coverage. Built specifically for Indian equity & options traders." },
            { icon: "💬", title: "Expert Support",    desc: "Get help from our trading experts via chat, email, or phone." },
            { icon: "🏷️", title: "Annual Savings",    desc: "Save up to 20% when you choose annual billing over monthly." },
          ].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.title}>
              <Box sx={{
                background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0",
                boxShadow: "0 1px 6px rgba(0,0,0,0.07)", p: 3, height: "100%",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 24px rgba(4,52,58,0.12)" },
              }}>
                <Typography variant="h3" sx={{ mb: 1.5 }}>{item.icon}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#04343a", mb: 1 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.7 }}>{item.desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ════════════════════════════════
          FAQ
      ════════════════════════════════ */}
      <Box sx={{ background: "#fff", py: 8, px: { xs: 2, md: 8 } }}>
        <Typography variant="h4" align="center" sx={{ fontWeight: 800, color: "#04343a", mb: 1 }}>Frequently Asked Questions</Typography>
        <Typography variant="body1" align="center" sx={{ color: "#64748b", mb: 5 }}>Have questions? We have answers.</Typography>
        <Box sx={{ maxWidth: 800, mx: "auto" }}>
          {faqs.map((faq, idx) => (
            <Accordion key={idx} sx={{
              mb: 1.5, borderRadius: "10px !important", border: "1px solid #e2e8f0",
              "&:before": { display: "none" }, boxShadow: "none",
              "&.Mui-expanded": { boxShadow: "0 4px 12px rgba(4,52,58,0.08)" },
            }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#016551" }} />} sx={{ px: 3 }}>
                <Typography sx={{ fontWeight: 700, color: "#04343a" }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Typography sx={{ color: "#64748b", lineHeight: 1.8 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ════════════════════════════════
          FINAL CTA
      ════════════════════════════════ */}
      <Box sx={{ background: "linear-gradient(135deg, #016551 0%, #04343a 100%)", py: 9, px: { xs: 3, md: 8 }, textAlign: "center" }}>
        <Typography variant="h3" sx={{ color: "#fff", fontWeight: 800, mb: 2, fontSize: { xs: "1.8rem", md: "2.5rem" } }}>
          Ready to Trade Smarter?
        </Typography>
        <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.7)", mb: 5, maxWidth: 500, mx: "auto", fontWeight: 400 }}>
          Join 10,000+ traders already using Stockyaari to make better decisions every day.
        </Typography>
        <Box component="button" onClick={scrollToPlans} sx={{
          background: "#fff", color: "#016551", border: "none", borderRadius: "8px",
          px: 6, py: 1.8, fontWeight: 700, fontSize: "1.05rem", cursor: "pointer",
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)", transition: "all 0.2s",
          "&:hover": { background: "#eef2f7", transform: "translateY(-2px)" },
        }}>
          Get Started Today →
        </Box>
      </Box>

    </Box>
  );
};

export default PricingSection;
